import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { MagneticButton } from "../components/MagneticButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEBSITE_TYPES = [
    "Business Website",
    "Ministry / Church Website",
    "Personal Brand Website",
    "Landing Page",
    "Event Website",
    "Website Redesign",
    "Not Sure Yet",
];

const inputCls =
    "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-sm text-white placeholder:text-slate-600 transition-[border-color,background-color,box-shadow] duration-300 focus:border-sky-400/60 focus:bg-white/[0.06] focus:shadow-[0_0_0_3px_rgba(0,102,255,0.15)] focus:outline-none";

const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.28em] text-slate-400";

export const Contact = () => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", business: "", website_type: "", vision: "" });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/contact`, form);
            setSent(true);
            toast.success("Message sent. Rodney will be in touch soon.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contact" className="relative py-24 md:py-36" data-testid="contact-section">
            <div className="absolute left-1/2 top-0 h-px w-2/3 -translate-x-1/2 bg-gradient-to-r from-transparent via-[#0066FF]/40 to-transparent" aria-hidden="true" />
            <div className="mx-auto grid max-w-7xl gap-16 px-4 sm:px-6 lg:grid-cols-[1fr_1.15fr] lg:gap-20 lg:px-8">
                <div>
                    <Reveal>
                        <Eyebrow>Contact</Eyebrow>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 font-display text-4xl font-extrabold tracking-tight text-[#0A0F1D] sm:text-5xl lg:text-6xl">
                            LET&apos;S BUILD<br /><span className="text-electric-grad">SOMETHING.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.18}>
                        <p className="mt-6 max-w-md text-sm leading-relaxed text-slate-600 sm:text-base">
                            Tell me about your vision — the business, the ministry, the idea. I&apos;ll personally
                            review every message and help you figure out the right next step.
                        </p>
                    </Reveal>
                </div>

                <Reveal delay={0.15}>
                    <div className="relative border-2 border-[#0A0F1D] bg-[#0A0F1D] p-6 shadow-[12px_12px_0_rgba(0,102,255,0.8)] sm:p-9">
                        <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[#0066FF]/15 blur-[90px]" aria-hidden="true" />
                        {sent ? (
                            <div className="relative flex min-h-[420px] flex-col items-center justify-center text-center" data-testid="contact-success">
                                <CheckCircle2 className="h-14 w-14 text-sky-400" />
                                <h3 className="mt-6 font-display text-2xl font-extrabold text-white sm:text-3xl">MESSAGE RECEIVED.</h3>
                                <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-400">
                                    Your vision is in good hands. Rodney will personally review it and get back to you soon.
                                </p>
                                <button
                                    data-testid="contact-send-another"
                                    onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", business: "", website_type: "", vision: "" }); }}
                                    className="mt-8 font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 underline-offset-4 hover:underline"
                                >
                                    Send another message
                                </button>
                            </div>
                        ) : (
                            <form data-testid="contact-form" onSubmit={submit} className="relative grid gap-5">
                                <div className="grid gap-5 sm:grid-cols-2">
                                    <div>
                                        <label htmlFor="cf-name" className={labelCls}>Name *</label>
                                        <input id="cf-name" data-testid="contact-name-input" required minLength={1} maxLength={120} className={inputCls} placeholder="Your name" value={form.name} onChange={set("name")} />
                                    </div>
                                    <div>
                                        <label htmlFor="cf-email" className={labelCls}>Email *</label>
                                        <input id="cf-email" data-testid="contact-email-input" required type="email" className={inputCls} placeholder="you@example.com" value={form.email} onChange={set("email")} />
                                    </div>
                                </div>
                                <div className="grid gap-5 sm:grid-cols-2">
                                    <div>
                                        <label htmlFor="cf-phone" className={labelCls}>Phone</label>
                                        <input id="cf-phone" data-testid="contact-phone-input" type="tel" maxLength={40} className={inputCls} placeholder="(555) 555-5555" value={form.phone} onChange={set("phone")} />
                                    </div>
                                    <div>
                                        <label htmlFor="cf-business" className={labelCls}>Business / Organization</label>
                                        <input id="cf-business" data-testid="contact-business-input" maxLength={160} className={inputCls} placeholder="Your brand or ministry" value={form.business} onChange={set("business")} />
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="cf-type" className={labelCls}>Type of Website *</label>
                                    <select id="cf-type" data-testid="contact-type-select" required className={`${inputCls} appearance-none`} value={form.website_type} onChange={set("website_type")}>
                                        <option value="" disabled>Select one…</option>
                                        {WEBSITE_TYPES.map((t) => (
                                            <option key={t} value={t}>{t}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="cf-vision" className={labelCls}>Tell Me About Your Vision *</label>
                                    <textarea id="cf-vision" data-testid="contact-vision-input" required minLength={1} maxLength={4000} rows={5} className={`${inputCls} resize-none`} placeholder="What are you building? Who is it for? What should it feel like?" value={form.vision} onChange={set("vision")} />
                                </div>
                                <MagneticButton testid="contact-submit-button" type="submit" disabled={sending} className="mt-1 w-full sm:w-auto">
                                    {sending ? (
                                        <>Sending <Loader2 className="h-4 w-4 animate-spin" /></>
                                    ) : (
                                        <>Send Your Vision <Send className="h-4 w-4" /></>
                                    )}
                                </MagneticButton>
                            </form>
                        )}
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
